import React, { useState, useRef, useEffect } from 'react';
import './admin-settings.css';
import Form from 'react-bootstrap/Form';
import CloseIcon from '../images/close.png';
import DocIcon from '../images/doc-icon.png';
import { getData, postData } from '../utils/apis/api';
import Loader from '../components/loader';
import CustomToast from '../components/custom-toaster';

const AdminSettings = ({ onClose, onSave, agentName = '', isEdit = false }) => {
  console.log(isEdit);

  // State for form fields
  const [systemMessage, setSystemMessage] = useState('');
  const [currentAgentName, setCurrentAgentName] = useState(agentName);
  const [model, setModel] = useState('gpt');
  const [temperature, setTemperature] = useState(0);
  const [presencePenalty, setPresencePenalty] = useState(0.0);
  const [frequencyPenalty, setFrequencyPenalty] = useState(0.0);
  const [topP, setTopP] = useState(0.0);
  const [maxTokens, setMaxTokens] = useState(0);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [capabilities, setCapabilities] = useState({
    web_search: false,
    rag: false,
  });

  // New state for uploaded image
  const [uploadedImagePreview, setUploadedImagePreview] = useState(null);
  const [uploadedImageFile, setUploadedImageFile] = useState(null);

  // Toast state
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState('success');
  const [isUploading, setIsUploading] = useState(false);
  const [generatedLink, setGeneratedLink] = useState('');

  // Refs
  const filesContainerRef = useRef(null);

  // Prefill form data when editing
  useEffect(() => {
    if (isEdit && agentName) {
      fetchConfigData();
    }
  }, [isEdit, agentName]);

  const fetchConfigData = async () => {
    setIsUploading(true);
    try {
      const response = await postData(`get_configs?agent_name=${agentName}`);
      const { icon, config } = response;
      setCurrentAgentName(config.agent_name || '');
      setModel(config.model || 'gpt');
      setTemperature(config.temperature || 0);
      setPresencePenalty(config.presence_penalty || 0.0);
      setFrequencyPenalty(config.frequency_penalty || 0.0);
      setTopP(config.top_p || 0.0);
      setMaxTokens(config.max_tokens || 0);
      setCapabilities({
        web_search: config.tools.includes('web_search'),
        rag: config.tools.includes('rag'),
      });
      setSystemMessage(config.system_prompt || '');
      setUploadedFiles(config.uploaded_files || []);
      if (icon) {
        setUploadedImagePreview(`data:image/png;base64,${icon}`);
      }
      showToastMessage('Configuration data loaded successfully.', 'success');
    } catch (error) {
      showToastMessage('Error fetching configuration data.', 'error');
    } finally {
      setIsUploading(false);
    }
  };

  // Handle image upload
  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setUploadedImageFile(file); // Save the binary file to be sent to the server

      // Create a preview for the UI
      const reader = new FileReader();
      reader.onload = (event) => {
        setUploadedImagePreview(event.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Show Toast function
  const showToastMessage = (message, type = 'success') => {
    setToastMessage(message);
    setToastType(type);
    setShowToast(true);
  };

  // Handle Save or Update
  const handleSaveOrUpdate = async () => {
    if (!isFormValid()) {
      showToastMessage('Please fill in all required fields.', 'warning');
      return;
    }
  
    setIsUploading(true);
    try {
      // Determine tools value based on the checkboxes selected.
      let toolsValue = 'standard';
      if (capabilities.web_search && capabilities.rag) {
        toolsValue = 'combined';
      } else if (capabilities.web_search) {
        toolsValue = 'websearch';
      } else if (capabilities.rag) {
        toolsValue = 'rag';
      }
  
      const formData = new FormData();
  
      // Append uploaded files
      if (uploadedFiles) {
        for (let i = 0; i < uploadedFiles.length; i++) {
          formData.append('files', uploadedFiles[i]);
        }
      }
  
      // Append uploaded image as binary file
      if (uploadedImageFile) {
        formData.append('icon', uploadedImageFile);
      }
  
      // Append other form fields
      formData.append('admin_name', sessionStorage.getItem('user_id'));
      formData.append('agent_name', currentAgentName);
      formData.append('model', model);
      formData.append('temperature', temperature);
      formData.append('system_message', systemMessage);
      // Use the new tools value here
      formData.append('tools', toolsValue);
      formData.append('presence_penalty', presencePenalty);
      formData.append('frequency_penalty', frequencyPenalty);
      formData.append('top_p', topP);
      formData.append('max_tokens', maxTokens);
      formData.append(
        'agent_url',
        `https://tranquilla.mresult.com?agentName=${currentAgentName}`
      );
  
      // API endpoint
      const endpoint = isEdit ? '/update_configuration' : '/create_configuration';
      const response = await postData(endpoint, formData);
  
      showToastMessage(
        `Configuration ${isEdit ? 'updated' : 'created'} successfully.`,
        'success'
      );
      setGeneratedLink(response.url);
  
      setTimeout(() => {
        onSave(response);
        setIsUploading(false);
        const element = document.getElementById("scrollarea");
        if (element) {
          element.scrollTop = element.scrollHeight;
        }
      }, 3000);
    } catch (error) {
      showToastMessage(
        `Error ${isEdit ? 'updating' : 'creating'} configuration.`,
        'error'
      );
    } finally {
      setIsUploading(false);
    }
  };

  const handleCheckboxChange = (event) => {
    const { id, checked } = event.target;
    setCapabilities((prev) => ({
      ...prev,
      [id]: checked,
    }));
  };

  const handleClose = () => {
    onClose();
  };

  const handleFileUpload = (event) => {
    const files = Array.from(event.target.files);
    setUploadedFiles((prevFiles) => [...prevFiles, ...files]);
  };

  const handleFileDelete = (index) => {
    setUploadedFiles((prevFiles) => prevFiles.filter((_, i) => i !== index));
  };

  const handleShowToast = (message) => {
    setToastMessage(message);
    setShowToast(true);
    setTimeout(() => {
      setShowToast(false);
    }, 5000);
  };

  // Function to check if mandatory fields are filled
  const isFormValid = () => {
    return currentAgentName.trim() !== '' && maxTokens > 0 && systemMessage.trim() !== '';
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      {isUploading && <Loader />}
      <div id="scrollarea" className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h5>
            {isEdit ? 'Update GPT Configuration' : 'Create GPT Configuration'}
          </h5>
          <button className="modal-close" onClick={onClose}>
            <img src={CloseIcon} alt="close" width="10" />
          </button>
        </div>

        <div>
          {/* Image Upload Area */}
          <div className="image-upload-container">
            <label htmlFor="imageUpload" className="image-upload-label">
              {uploadedImagePreview ? (
                <img
                  src={uploadedImagePreview}
                  alt="Uploaded"
                  className="uploaded-image"
                />
              ) : (
                <span className="upload-placeholder">
                  <div>Upload Icon</div>
                  <div>+</div>
                </span>
              )}
            </label>
            <input
              type="file"
              id="imageUpload"
              accept="image/*"
              hidden
              onChange={handleImageUpload}
            />
          </div>
          <div className="form-container">
            {/* Left and Right Form Sections */}
            {/* Left Section */}

            <div className="form-left">
              <Form.Label htmlFor="name">
                Name <span style={{ color: 'red' }}>*</span>
              </Form.Label>
              <Form.Control
                type="text"
                value={currentAgentName}
                onChange={(e) => setCurrentAgentName(e.target.value)}
                placeholder="Name your GPT"
                className="form-control my-2"
                disabled={isEdit} // Disable the input field when updating
              />

              <Form.Group controlId="maxTokensInput">
                <Form.Label>
                  Max Tokens <span style={{ color: 'red' }}>*</span>
                </Form.Label>
                <Form.Control
                  type="number"
                  value={maxTokens}
                  onChange={(e) => setMaxTokens(parseInt(e.target.value))}
                  className="my-2"
                  placeholder="e.g., 1500"
                />
              </Form.Group>

              <Form.Group controlId="topPInput">
                <Form.Label>Top P</Form.Label>
                <Form.Control
                  type="number"
                  step="0.1"
                  value={topP}
                  onChange={(e) => setTopP(parseFloat(e.target.value))}
                  className="my-2"
                  placeholder="0.0 - 1.0"
                />
              </Form.Group>

              <div className="form-group">
                <label className="form-label">Capabilities</label>
                <Form.Check
                  type="checkbox"
                  id="web_search"
                  label="Web browsing"
                  checked={capabilities.web_search}
                  onChange={handleCheckboxChange}
                />
                <Form.Check
                  type="checkbox"
                  id="rag"
                  label="RAG Vector Search"
                  checked={capabilities.rag}
                  onChange={handleCheckboxChange}
                />
              </div>

              <Form.Group controlId="knowledge">
                <Form.Label>Knowledge</Form.Label>
                <Form.Text className="text-muted">
                  If you upload files under knowledge, conversation with your
                  GPT includes file contents. Files can be downloaded when the
                  code interpreter is enabled.
                </Form.Text>
              </Form.Group>

              <input
                type="file"
                id="upload"
                hidden
                onChange={handleFileUpload}
              />
              <label htmlFor="upload" id="uploadLabel">
                Upload file
              </label>

              {uploadedFiles.length > 0 && (
                <div className="uploaded-files" ref={filesContainerRef}>
                  <h6>Uploaded Files</h6>
                  {uploadedFiles.map((file, index) => (
                    <div className="file-item" key={index}>
                      <img src={DocIcon} alt="Document" width="14" />
                      <span className="file-name">{file.name}</span>
                      <span className="file-size">
                        {(file.size / 1024).toFixed(2)} kb
                      </span>
                      <button
                        className="delete-file"
                        onClick={() => handleFileDelete(index)}
                      >
                        <img src={CloseIcon} alt="close" width="10" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Right Section */}
            <div className="form-right">
              <Form.Group controlId="modelSelect">
                <Form.Label>Model</Form.Label>
                <Form.Control
                  as="select"
                  value={model}
                  onChange={(e) => setModel(e.target.value)}
                  className="my-2"
                >
                  <option value="GPT-4">GPT-4o</option>
                  <option value="GPT-4-turbo">GPT-4o-mini</option>
                </Form.Control>
              </Form.Group>

              <Form.Group controlId="temperatureInput">
                <Form.Label>Temperature</Form.Label>
                <Form.Control
                  type="number"
                  step="0.1"
                  value={temperature}
                  onChange={(e) => setTemperature(parseFloat(e.target.value))}
                  className="my-2"
                  placeholder="0.0 - 1.0"
                />
              </Form.Group>

              <Form.Group controlId="presencePenaltyInput">
                <Form.Label>Presence Penalty</Form.Label>
                <Form.Control
                  type="number"
                  step="0.1"
                  value={presencePenalty}
                  onChange={(e) =>
                    setPresencePenalty(parseFloat(e.target.value))
                  }
                  className="my-2"
                  placeholder="0.0 - 2.0"
                />
              </Form.Group>

              <Form.Group controlId="frequencyPenaltyInput">
                <Form.Label>Frequency Penalty</Form.Label>
                <Form.Control
                  type="number"
                  step="0.1"
                  value={frequencyPenalty}
                  onChange={(e) =>
                    setFrequencyPenalty(parseFloat(e.target.value))
                  }
                  className="my-2"
                  placeholder="0.0 - 2.0"
                />
              </Form.Group>

              <Form.Group controlId="instructions">
                <Form.Label>
                  Instructions <span style={{ color: 'red' }}>*</span>
                </Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={systemMessage}
                  onChange={(e) => setSystemMessage(e.target.value)}
                  className="my-2"
                  placeholder="What does GPT do? How does it behave? What should it avoid doing?"
                />
              </Form.Group>
            </div>
          </div>
          {generatedLink && (
            <div>
              <div className="ps-4">Sharable link:</div>
              <div className="GeneratedLink pt-3">{generatedLink}</div>
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button type="button" onClick={handleClose} className="cancel-button">
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSaveOrUpdate}
            className="save-button"
            disabled={!isFormValid()} // Disable if any mandatory field is empty
          >
            {isEdit ? 'Update' : 'Create'}
          </button>
        </div>

        {/* CustomToast */}
        <CustomToast
          show={showToast}
          onClose={() => setShowToast(false)}
          message={toastMessage}
          type={toastType}
        />
      </div>
    </div>
  );
};

export default AdminSettings;